import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Dialog,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
} from '@mui/material';
import {
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Mic as MicIcon,
  Close as CloseIcon,
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  ErrorOutline as ErrorIcon,
  Reply as ReplyIcon,
  Edit as EditIcon,
  Forward as ForwardIcon,
  DeleteOutline as DeleteIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { mediaSrc } from '../../api/conversations';

const FIFTEEN_MIN_MS = 15 * 60 * 1000;

const K = {
  blue: '#4185F1',
  navy: '#121826',
  greyBubble: '#EEF1F8',
};

const renderStatusTicks = (status) => {
  // Outbound message status (operator -> customer): sent / delivered / read / Visto / failed.
  // 'Visto' was the legacy local marker; treat it as 'read' when our DB sets it.
  const TICK = { fontSize: 14, color: 'rgba(255,255,255,0.85)' };
  if (status === 'failed') {
    return <ErrorIcon sx={{ fontSize: 14, color: '#FFB4B4' }} />;
  }
  if (status === 'read' || status === 'Visto') {
    return <DoneAllIcon sx={{ ...TICK, color: '#7BD0FF' }} />;
  }
  if (status === 'delivered') {
    return <DoneAllIcon sx={TICK} />;
  }
  // sent / pending / unknown
  return <DoneIcon sx={TICK} />;
};

const formatBytes = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const MediaImage = ({ mediaId, onOpen }) => {
  const url = mediaSrc(mediaId);
  if (!url) return null;
  return (
    <Box
      component="img"
      src={url}
      alt=""
      loading="lazy"
      decoding="async"
      onClick={() => onOpen(url)}
      sx={{
        maxWidth: 280,
        maxHeight: 320,
        borderRadius: 1,
        cursor: 'zoom-in',
        display: 'block',
      }}
    />
  );
};

const MediaVideo = ({ mediaId }) => {
  const url = mediaSrc(mediaId);
  if (!url) return null;
  return (
    <Box
      component="video"
      src={url}
      controls
      preload="metadata"
      sx={{ maxWidth: 320, maxHeight: 320, borderRadius: 1, display: 'block' }}
    />
  );
};

const MediaAudio = ({ mediaId, voice }) => {
  const url = mediaSrc(mediaId);
  if (!url) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 220 }}>
      {voice && <MicIcon fontSize="small" sx={{ opacity: 0.7 }} />}
      <Box component="audio" src={url} controls preload="metadata" sx={{ height: 32, flex: 1 }} />
    </Box>
  );
};

const MediaDocument = ({ mediaId, filename, sizeBytes }) => {
  const url = mediaSrc(mediaId);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: 1.25,
        bgcolor: 'rgba(0,0,0,0.04)',
        borderRadius: 1,
        minWidth: 240,
      }}
    >
      <FileIcon sx={{ color: K.navy }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {filename || 'Documento'}
        </Typography>
        {sizeBytes ? (
          <Typography variant="caption" sx={{ opacity: 0.6 }}>
            {formatBytes(sizeBytes)}
          </Typography>
        ) : null}
      </Box>
      {url && (
        <IconButton
          size="small"
          component="a"
          href={url}
          download={filename || 'archivo'}
          sx={{ color: K.navy }}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

const MediaLocation = ({ location }) => {
  if (!location) return null;
  const { lat, lng, name, address } = location;
  const href = `https://www.google.com/maps?q=${lat},${lng}`;
  return (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        p: 1.25,
        bgcolor: 'rgba(0,0,0,0.04)',
        borderRadius: 1,
        textDecoration: 'none',
        color: 'inherit',
        minWidth: 220,
      }}
    >
      <LocationIcon sx={{ color: K.blue, mt: 0.25 }} />
      <Box sx={{ minWidth: 0 }}>
        {name && <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>}
        {address && <Typography variant="caption" sx={{ display: 'block', opacity: 0.75 }}>{address}</Typography>}
        {!name && !address && (
          <Typography variant="body2">{lat}, {lng}</Typography>
        )}
      </Box>
    </Box>
  );
};

const MediaContacts = ({ contacts }) => {
  if (!Array.isArray(contacts) || contacts.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {contacts.map((c, i) => {
        const name = c?.name?.formatted_name || 'Contacto';
        const phone = c?.phones?.[0]?.phone || '';
        return (
          <Box
            key={i}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              bgcolor: 'rgba(0,0,0,0.04)',
              borderRadius: 1,
              minWidth: 220,
            }}
          >
            <PersonIcon sx={{ color: K.navy }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>
              {phone && <Typography variant="caption" sx={{ opacity: 0.75 }}>{phone}</Typography>}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const InteractiveButtons = ({ interactive }) => {
  if (!interactive || !Array.isArray(interactive.options) || interactive.options.length === 0) {
    return null;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 220 }}>
      {interactive.bodyText && (
        <Typography variant="body2" sx={{ wordBreak: 'break-word', mb: 0.5 }}>
          {interactive.bodyText}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          borderTop: '1px solid rgba(18, 24, 38, 0.08)',
          pt: 0.5,
        }}
      >
        {interactive.options.map((o, i) => (
          <Box
            key={`${o.id || i}`}
            sx={{
              textAlign: 'center',
              py: 0.75,
              fontSize: '0.85rem',
              fontWeight: 600,
              color: K.blue,
              borderRadius: 0.75,
              border: '1px solid rgba(65, 133, 241, 0.25)',
              bgcolor: 'rgba(65, 133, 241, 0.04)',
              cursor: 'default',
            }}
          >
            {o.title}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const previewForQuoted = (m) => {
  if (!m) return '';
  if (m.content) return m.content;
  if (m.caption) return m.caption;
  switch (m.messageType) {
    case 'image': return '📷 Foto';
    case 'video': return '🎥 Video';
    case 'audio': return m.voice ? '🎤 Nota de voz' : '🔊 Audio';
    case 'document': return m.filename ? `📎 ${m.filename}` : '📎 Documento';
    case 'location': return '📍 Ubicación';
    case 'contacts': return '👤 Contacto';
    case 'interactive': return '☰ Menú';
    default: return '';
  }
};

export const MessageBubble = ({
  message,
  onReply,
  onEdit,
  onForward,
  onDelete,
  quotedMessage,
  onJumpToQuoted,
}) => {
  const isAssistant = message.type === 'agent';
  const isCustomer = message.type === 'customer';
  const isSystem = message.type === 'system';
  const status = message.status;
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(message.content || '');

  const canEdit = isAssistant
    && message.messageType === 'text'
    && message.waMessageId
    && (Date.now() - new Date(message.timestamp).getTime() < FIFTEEN_MIN_MS);

  const handleEditCommit = async () => {
    const next = editValue.trim();
    if (!next || next === message.content) {
      setEditing(false);
      return;
    }
    try {
      await onEdit(message, next);
      setEditing(false);
    } catch (e) {
      // toast handled upstream
    }
  };

  if (isSystem) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Paper
          elevation={0}
          sx={{
            px: 1.5,
            py: 0.75,
            maxWidth: '85%',
            bgcolor: 'grey.200',
            borderRadius: 1.5,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {message.content}
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (message.isInternal) {
    return (
      <Box data-mid={String(message.id)} sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Paper
          elevation={0}
          sx={{
            px: 1.5,
            py: 1,
            maxWidth: '88%',
            width: '88%',
            bgcolor: '#FFF8C5',
            border: '1px solid #FFE066',
            borderRadius: 1,
            color: '#5C4B00',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.04em', display: 'block', mb: 0.25 }}>
            🗒 NOTA INTERNA{message.authorName ? ` · ${message.authorName}` : ''}
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
            {message.content}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7, fontSize: '0.7rem' }}>
            {format(new Date(message.timestamp), 'HH:mm')}
          </Typography>
        </Paper>
      </Box>
    );
  }

  const messageType = message.messageType || 'text';
  const hasMedia = messageType !== 'text';

  let mediaNode = null;
  if (messageType === 'image') {
    mediaNode = <MediaImage mediaId={message.mediaId} onOpen={setLightboxUrl} />;
  } else if (messageType === 'video') {
    mediaNode = <MediaVideo mediaId={message.mediaId} />;
  } else if (messageType === 'audio') {
    mediaNode = <MediaAudio mediaId={message.mediaId} voice={message.voice} />;
  } else if (messageType === 'document') {
    mediaNode = (
      <MediaDocument
        mediaId={message.mediaId}
        filename={message.filename}
        sizeBytes={message.sizeBytes}
      />
    );
  } else if (messageType === 'location') {
    mediaNode = <MediaLocation location={message.location} />;
  } else if (messageType === 'contacts') {
    mediaNode = <MediaContacts contacts={message.contacts} />;
  } else if (messageType === 'interactive') {
    const opts = message.interactive?.options;
    if (Array.isArray(opts) && opts.length > 0) {
      mediaNode = <InteractiveButtons interactive={message.interactive} />;
    }
  }

  // Caption (or content for non-media messages). Interactive renders its own
  // body_text inside the buttons block, so don't duplicate it below — but if
  // the buttons couldn't render (no options blob), fall back to `content`.
  const textBelow =
    messageType === 'interactive'
      ? (mediaNode ? null : message.content)
      : (hasMedia ? message.caption : message.content);

  return (
    <>
      <Box
        data-mid={String(message.id)}
        sx={{
          display: 'flex',
          justifyContent: isCustomer ? 'flex-end' : 'flex-start',
          mb: 2,
          alignItems: 'center',
          gap: 0.5,
          '& .reply-action': { opacity: 0, transition: 'opacity .15s' },
          '&:hover .reply-action': { opacity: 1 },
        }}
      >
        {(onReply || onEdit || onForward || onDelete) && !editing && (
          <IconButton
            size="small"
            className="reply-action"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{ color: 'rgba(18,24,38,0.55)' }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
        <Menu
          anchorEl={menuAnchor}
          open={!!menuAnchor}
          onClose={() => setMenuAnchor(null)}
        >
          {onReply && (
            <MenuItem onClick={() => { setMenuAnchor(null); onReply(message); }}>
              <ListItemIcon><ReplyIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Responder</ListItemText>
            </MenuItem>
          )}
          {canEdit && onEdit && (
            <MenuItem onClick={() => { setMenuAnchor(null); setEditValue(message.content || ''); setEditing(true); }}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Editar</ListItemText>
            </MenuItem>
          )}
          {onForward && (
            <MenuItem onClick={() => { setMenuAnchor(null); onForward(message); }}>
              <ListItemIcon><ForwardIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Reenviar</ListItemText>
            </MenuItem>
          )}
          {onDelete && (
            <MenuItem onClick={() => { setMenuAnchor(null); onDelete(message); }}>
              <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Eliminar para mí</ListItemText>
            </MenuItem>
          )}
        </Menu>
        <Paper
          elevation={0}
          sx={{
            p: hasMedia ? 1 : 1.5,
            maxWidth: '72%',
            bgcolor: isCustomer ? K.blue : K.greyBubble,
            color: isCustomer ? '#fff' : K.navy,
            borderRadius: 1.5,
            border: isAssistant ? '1px solid rgba(18, 24, 38, 0.06)' : 'none',
          }}
        >
          {message.replyToId && quotedMessage && (
            <Box
              onClick={() => onJumpToQuoted && onJumpToQuoted(message.replyToId)}
              sx={{
                cursor: onJumpToQuoted ? 'pointer' : 'default',
                borderLeft: `3px solid ${isCustomer ? 'rgba(255,255,255,0.55)' : K.blue}`,
                bgcolor: isCustomer ? 'rgba(255,255,255,0.10)' : 'rgba(65,133,241,0.08)',
                px: 1, py: 0.5,
                borderRadius: 0.75,
                mb: 0.75,
                fontSize: '0.78rem',
                opacity: 0.92,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', opacity: 0.85 }}>
                {quotedMessage.type === 'agent' ? 'Tú' : (quotedMessage.agent || 'Cliente')}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {previewForQuoted(quotedMessage) || '…'}
              </Typography>
            </Box>
          )}
          {isAssistant && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 0.5,
                px: hasMedia ? 0.5 : 0,
                fontWeight: 700,
                color: 'rgba(18, 24, 38, 0.55)',
                fontSize: '0.65rem',
                letterSpacing: '0.04em',
              }}
            >
              KOBRA ASSISTANT
            </Typography>
          )}
          {message.agent && !isAssistant && (
            <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', mb: 0.5, px: hasMedia ? 0.5 : 0 }}>
              {message.agent}
            </Typography>
          )}

          {message.forwarded && (
            <Typography
              variant="caption"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                opacity: 0.7,
                fontStyle: 'italic',
                mb: 0.5,
                px: hasMedia ? 0.5 : 0,
              }}
            >
              <ForwardIcon sx={{ fontSize: 12 }} /> Reenviado
            </Typography>
          )}

          {mediaNode && <Box sx={{ mb: textBelow ? 0.75 : 0 }}>{mediaNode}</Box>}

          {editing ? (
            <TextField
              autoFocus
              multiline
              fullWidth
              size="small"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleEditCommit();
                } else if (e.key === 'Escape') {
                  setEditing(false);
                }
              }}
              onBlur={() => setEditing(false)}
              sx={{
                '& .MuiInputBase-root': {
                  bgcolor: 'rgba(255,255,255,0.18)',
                  color: 'inherit',
                  fontSize: '0.875rem',
                },
                '& fieldset': { border: 'none' },
              }}
            />
          ) : textBelow && (
            <Typography
              variant="body2"
              sx={{ wordBreak: 'break-word', px: hasMedia ? 0.5 : 0 }}
            >
              {textBelow}
            </Typography>
          )}

          {message.editedAt && !editing && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.25,
                px: hasMedia ? 0.5 : 0,
                opacity: 0.7,
                fontStyle: 'italic',
                fontSize: '0.65rem',
              }}
            >
              editado
            </Typography>
          )}

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.75,
              px: hasMedia ? 0.5 : 0,
              opacity: isCustomer ? 0.85 : 0.55,
              fontSize: '0.7rem',
            }}
          >
            {format(new Date(message.timestamp), 'HH:mm')}
          </Typography>
          {isCustomer && status && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', mt: 0.25, ml: 0.5 }}>
              {renderStatusTicks(status)}
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog
        open={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
        maxWidth="lg"
        PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.92)', boxShadow: 'none' } }}
      >
        <IconButton
          onClick={() => setLightboxUrl(null)}
          sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>
        {lightboxUrl && (
          <Box
            component="img"
            src={lightboxUrl}
            alt=""
            sx={{ maxWidth: '90vw', maxHeight: '90vh', display: 'block' }}
          />
        )}
      </Dialog>
    </>
  );
};
